import { useEffect, useRef, useState } from 'react';

import type { Notification } from '../../interfaces/INotificationsApi.js';
import { newArrivals } from '../newArrivals.js';
import { useNotifications } from './useNotifications.js';

export interface IArrivals {
  /** How many times the list has gained something since first shown; moves once per arrival. */
  count: number;
  /** What the last arrival brought. */
  arrived: readonly Notification[];
}

const NOTHING: IArrivals = { count: 0, arrived: [] };

/**
 * The site's arrivals: a count that moves once each time the site's list
 * gains something, and what that something was — for a sound, a ring or a
 * peek to answer to (`newArrivals` says what counts).
 *
 * The first list the server sends for a site is the baseline, not an
 * arrival: nothing sounds on load, and nothing sounds on a site switch. A
 * list the cache shows while that first answer is on its way is no baseline
 * either — it is the site as it was when last shown, and comparing the
 * answer with it would sound for everything that came in while the person
 * was elsewhere.
 */
export function useArrivals(siteId: string | null): IArrivals {
  const { data, isFetchedAfterMount } = useNotifications(siteId);
  // Only what the server has answered since this site was picked up.
  const notifications = isFetchedAfterMount ? data?.notifications : undefined;

  const [arrivals, setArrivals] = useState(NOTHING);
  const baseline = useRef<{ siteId: string | null; notifications: readonly Notification[] } | null>(null);

  useEffect(() => {
    if (!notifications) return; // not answered yet: the baseline stays what it was
    const previous = baseline.current;
    baseline.current = { siteId, notifications };
    if (!previous || previous.siteId !== siteId) return;
    const arrived = newArrivals(previous.notifications, notifications);
    if (arrived.length > 0) setArrivals(({ count }) => ({ count: count + 1, arrived }));
  }, [siteId, notifications]);

  return arrivals;
}
